import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lightning, Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Password updated");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-emerald-600 flex items-center justify-center">
            <Lightning weight="fill" className="text-zinc-950" size={20} />
          </div>
          <span className="font-heading font-black text-lg text-zinc-50">DVEPL</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-2">Set New Password</div>
        <h2 className="font-heading text-3xl font-black tracking-tight text-zinc-50 mb-6">Reset password</h2>

        {!token ? (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400" data-testid="reset-invalid">This reset link is invalid or missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
              <ArrowLeft size={15} /> Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">New password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="reset-password"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2.5 pr-10 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 transition-colors"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-reset-password" aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-emerald-400 transition-colors">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Confirm password</label>
              <input
                type={showPw ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="reset-confirm"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2.5 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 transition-colors"
                placeholder="••••••••"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold h-11">
              {loading ? "Updating…" : "Update password"}
            </Button>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-emerald-300 transition-colors" data-testid="back-to-login">
              <ArrowLeft size={15} /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
